import EventLayout from '@/components/layouts/EventLayout'
import ContentSection from '@/components/shared/ContentSection'
import { Card, Tooltip } from '@/components/ui'
import { InfoCircleOutlined } from '@ant-design/icons'
import { HiOutlineClipboardList, HiOutlineUsers, HiOutlineClock } from 'react-icons/hi'
import { Head } from '@inertiajs/react'
import { EventStatus, EVENT_STATUS_LABELS } from '@/types/events'

interface ScanPointStat {
  id: number
  name: string
  count: number
}

interface EventShowProps {
  event: {
    id: number
    name: string
    slug: string
    status: EventStatus
    logo_url: string | null
  }
  stats: {
    registered: number
    attended: number
    pending_arrival: number
    attendance_percentage: number
    pending_percentage: number
  }
  scan_points: ScanPointStat[]
  last_updated: string
}

function StatCard({
  icon,
  label,
  value,
  percentage,
  tooltip,
}: {
  icon: React.ReactNode
  label: string
  value: number
  percentage?: number
  tooltip: string
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 text-gray-500 text-sm mb-1">
        {label}
        <Tooltip title={tooltip}>
          <InfoCircleOutlined className="text-xs cursor-help" />
        </Tooltip>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gray-400">{icon}</span>
        <span className="text-2xl font-semibold text-gray-900">
          {value.toLocaleString()}
        </span>
        {percentage !== undefined && (
          <span className="text-sm text-green-600">{percentage}%</span>
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: EventStatus }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full status-indicator-${status}`} />
      <span className="text-sm">{EVENT_STATUS_LABELS[status]}</span>
    </div>
  )
}

export default function EventShow({ event, stats, scan_points, last_updated }: EventShowProps) {
  return (
    <EventLayout>
      <Head title={`${event.name} - Dashboard`} />
      <ContentSection padding="lg">
        {/* Event Summary Card */}
        <Card style={{ border: '1px solid #4277BB' }}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-6">
                <h1 className="text-2xl font-semibold text-gray-900">{event.name}</h1>
                <StatusBadge status={event.status} />
              </div>

              <div className="flex gap-12">
                <StatCard
                  icon={<HiOutlineClipboardList className="text-xl" />}
                  label="Registered"
                  value={stats.registered}
                  tooltip="Total number of registrants for this event"
                />
                <StatCard
                  icon={<HiOutlineUsers className="text-xl" />}
                  label="Attended"
                  value={stats.attended}
                  percentage={stats.attendance_percentage}
                  tooltip="Number of registrants who have checked in"
                />
                <StatCard
                  icon={<HiOutlineClock className="text-xl" />}
                  label="Pending Arrival"
                  value={stats.pending_arrival}
                  percentage={stats.pending_percentage}
                  tooltip="Number of registrants who haven't checked in yet"
                />
              </div>
            </div>

            {/* Event Logo */}
            {event.logo_url && (
              <div className="w-24 h-24 rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  src={event.logo_url}
                  alt={`${event.name} logo`}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Scan Points Card */}
        <Card style={{ marginTop: '24px' }}>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-medium text-gray-900">
                Number of attendees for each Scan Point
              </h2>
              <Tooltip title="Check-in count for each scan point configured for this event">
                <InfoCircleOutlined className="text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <span className="text-sm text-gray-500">Last Updated: {last_updated}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {scan_points.map((scanPoint) => (
              <div key={scanPoint.id}>
                <div className="text-sm text-gray-500 mb-1">{scanPoint.name}</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {scanPoint.count.toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          {scan_points.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No scan points configured for this event.
            </div>
          )}
        </Card>
      </ContentSection>
    </EventLayout>
  )
}
